// Copyright (c) 2020-2023 Visual Intelligence LP. All rights reserved.
//
import Axios from 'axios';
import { injectable } from 'inversify';

import Deliverable,
{
  DeliverableDTO,
  DeliverableTypeDTO,
  DeliverableDownloadAccessDTO
} from '@shared/models/deliverable';
import BaseService from '@core/services/base';
import { REQUEST_PARAMS } from '@shared/constants/services';
import { ListRequestParams, ListRequestResponse } from '@shared/types/services';
import { DELIVERABLE_FIELDS } from '@shared/constants/deliverable';
import { Id } from '@shared/types/common';
import Datum from '@shared/models/deliverable/datum';
import Projection from '@shared/models/deliverable/projection';
import { DimensionDTO } from '@shared/models/deliverable/dimension';
import { TaskTreeItemDTO } from '@shared/models/deliverable/task-tree-item';
import { SDownloadInfo } from '@shared/models/sp-sheets';

@injectable()
export default class DeliverableService extends BaseService<DeliverableDTO, Deliverable> {
  static diToken = Symbol('deliverables-service');

  constructor() {
    super({ domainArea: 'deliverable', Model: Deliverable });
  }

  getDeliverableDatumsListQuery({ pagination, sorting, filtering }: ListRequestParams) {
    return `
      {
        datums (
          ${REQUEST_PARAMS.pagination.fields.page}: ${pagination.page}, 
          ${REQUEST_PARAMS.pagination.fields.pageSize}: ${pagination.pageSize}, 
          sort: { type: ${sorting?.type}, field: ${sorting?.field} },
          ${REQUEST_PARAMS.filtering.fields.searchKeyword}: "${filtering?.searchKeyword || ''}",
        ) {
          items {
            ${DELIVERABLE_FIELDS.id},
            ${DELIVERABLE_FIELDS.name},
          }
          pageInfo {
            totalCount,
            hasPreviousPage,
            hasNextPage
          }
        }
      }
    `;
  }

  getDeliverableProjectionsListQuery({ pagination, sorting, filtering }: ListRequestParams) {
    return `
      {
        projections (
          ${REQUEST_PARAMS.pagination.fields.page}: ${pagination.page}, 
          ${REQUEST_PARAMS.pagination.fields.pageSize}: ${pagination.pageSize}, 
          sort: { type: ${sorting?.type}, field: ${sorting?.field} },
          ${REQUEST_PARAMS.filtering.fields.searchKeyword}: "${filtering?.searchKeyword || ''}",
        ) {
          items {
            ${DELIVERABLE_FIELDS.id},
            ${DELIVERABLE_FIELDS.name},
          }
          pageInfo {
            totalCount,
            hasPreviousPage,
            hasNextPage
          }
        }
      }
    `;
  }

  async getDeliverableDatums(params: ListRequestParams) {
    const { data } = await Axios.get<{ datums: ListRequestResponse<Datum> }>(
      '/deliverable-datums',
      { params: { query: this.getDeliverableDatumsListQuery(params) } }
    );

    const { datums: { items, pageInfo } } = data;

    return {
      pageInfo,
      items: items.map((dto) => new Datum(dto)),
    };
  }

  async getDeliverableProjections(params: ListRequestParams) {
    const { data } = await Axios.get<{ projections: ListRequestResponse<Projection> }>(
      '/deliverable-projections',
      { params: { query: this.getDeliverableProjectionsListQuery(params) } }
    );

    const { projections: { items, pageInfo } } = data;

    return {
      pageInfo,
      items: items.map((dto) => new Projection(dto)),
    };
  }

  // Get array corresponding to the DeliverableType DB table.
  async getDeliverableTypes(): Promise<Array<DeliverableTypeDTO>> {
    const url = '/deliverable-types';
    const { data } = await Axios.get(url);

    return data;
  }

  // Get array corresponding to the dimension DB table.
  async getDeliverableDimensions(): Promise<Array<DimensionDTO>> {
    const url = '/deliverable-dimensions';
    const { data } = await Axios.get(url);

    return data;
  }

  // Fetch task tree from web-api (dimension and type DB tables.  Now replaced by workflow.json).
  async getTaskTree(initCode: number): Promise<Array<TaskTreeItemDTO>> {
    const url =  `/deliverable-task-tree?initCode=${initCode}`;
    const { data } = await Axios.get(url);

    return data;
  }


  updateDownloadInstruction(deliverableId: DeliverableDTO['id'], data: DeliverableDownloadAccessDTO): Promise<void> {
    return Axios.put(`${this.urlPrefix}/${deliverableId}/access`, data);
  }

  editDatumProjection(
    deliverableId: Id, 
    data: { projectionId?: Id, datumId?: Id }
  ): Promise<void> {
    return Axios.put(`${this.urlPrefix}/${deliverableId}/datum-projection`, data);
  }

  downloadCollectionData = async (deliverableId: string, apiCmd: string) => {
    const url = `/pilots/deliverable/${deliverableId}/${apiCmd}`;
    const response = await Axios({
      method: 'GET',
      url: url,
      responseType: 'blob',
    });
    return response ? { data: response.data, headers: response.headers } : { data: undefined, headers: undefined };
  }

  // Get the ACS file of all collections belonging to the job-request having this delivable
  downloadACS = async (deliverableId: string) => {
    return await this.downloadCollectionData(deliverableId, 'getacs');
  }

  // Get the Orbit QC file of all collections belonging to the job-request having this delivable
  downloadOrbitQCPdf = async (deliverableId: string) => {
    return await this.downloadCollectionData(deliverableId, 'getorbitqcpdf');
  }

  async getDownloadInstruction(deliverableId: DeliverableDTO['id']): Promise<DeliverableDownloadAccessDTO> {
    try {
      const { data } = await Axios.get(`${this.urlPrefix}/${deliverableId}/access`);

      return data;
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  // Download multiple collection data files.  
  // 'downloadInfo' holds the site name and type of data (ACS, PDF, ...) to download.
  downloadMultiples = async (downloadInfo: Array<SDownloadInfo>) => {
    const url = `/pilots/download-sites`;
    const response = await Axios({
      method: 'POST',
      url: url,
      headers: {
        'Content-Type': 'application/json', 
      },
      data: JSON.stringify(downloadInfo),
      responseType: 'blob',
    });

    return response ? { data: response.data, headers: response.headers } : { data: undefined, headers: undefined };
  }

}
